import { useEffect, useState, useRef, useCallback } from 'react'
import { Routes, Route, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Navigation } from './components/Navigation'
import { Dashboard } from './pages/Dashboard'
import { LogEvent } from './pages/LogEvent'
import { Statistics } from './pages/Statistics'
import { TripStatisticsPage } from './pages/TripStatistics'
import { TripLogsPage } from './pages/TripLogs'
import { Settings } from './pages/Settings'
import { Users } from './pages/Users'
import { Login } from './pages/Login'
import { Setup } from './pages/Setup'
import { ChangePassword } from './pages/ChangePassword'
import { AuthContext, type User, type AuthContextType } from './contexts/AuthContext'
import './App.css'

interface AuthStatus {
  authenticated: boolean
  needs_setup?: boolean
  user?: User
}

function App() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [authChecked, setAuthChecked] = useState(false)
  const [mustChangePassword, setMustChangePassword] = useState(false)
  const [needsSetup, setNeedsSetup] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const isLoginPage = location.pathname === '/login'
  const isSetupPage = location.pathname === '/setup'
  const isChangePasswordPage = location.pathname === '/change-password'
  const showNavigation = !isLoginPage && !isSetupPage && !(isChangePasswordPage && mustChangePassword)
  const checkingAuth = useRef(false)

  const checkAuth = useCallback(async () => {
    // Prevent concurrent auth checks
    if (checkingAuth.current) return
    checkingAuth.current = true

    try {
      const response = await fetch('/api/auth/status', { credentials: 'same-origin' })
      const data: AuthStatus = await response.json()

      if (!data.authenticated) {
        setUser(null)
        setNeedsSetup(!!data.needs_setup)
      } else if (data.user?.must_change_password) {
        setUser(data.user)
        setMustChangePassword(true)
        setNeedsSetup(false)
      } else {
        setUser(data.user || null)
        setMustChangePassword(false)
        setNeedsSetup(false)
      }
    } catch {
      setUser(null)
    } finally {
      checkingAuth.current = false
      setAuthChecked(true)
    }
  }, [])

  // Initial auth check on mount only
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // Handle redirects based on auth state and current path
  useEffect(() => {
    if (!authChecked) return

    if (!user) {
      if (needsSetup) {
        if (location.pathname !== '/setup') {
          navigate('/setup', { replace: true })
        }
      } else if (location.pathname !== '/login') {
        const returnUrl = location.pathname + location.search
        navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`, { replace: true })
      }
    } else if (mustChangePassword) {
      // Must change password
      if (location.pathname !== '/change-password') {
        navigate('/change-password', { replace: true })
      }
    } else {
      // Authenticated - redirect away from login page
      if (location.pathname === '/login' || location.pathname === '/setup') {
        const returnUrl = searchParams.get('returnUrl') || '/'
        navigate(returnUrl, { replace: true })
      }
      // Redirect guests away from admin pages
      else if (user.role === 'guest' && (location.pathname === '/users' || location.pathname === '/settings')) {
        navigate('/', { replace: true })
      }
    }
  }, [authChecked, user, mustChangePassword, needsSetup, location.pathname, location.search, searchParams, navigate])

  if (!authChecked) {
    return null
  }

  const authValue: AuthContextType = {
    user,
    isAdmin: user?.role === 'admin',
    setUser
  }

  return (
    <AuthContext.Provider value={authValue}>
      <div className="app">
        {showNavigation && <Navigation />}
        <main className={showNavigation ? 'main-content' : ''}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/setup" element={<Setup onAlreadySetup={() => setNeedsSetup(false)} />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/" element={<Dashboard />} />
            <Route path="/log-event" element={<LogEvent />} />
            <Route path="/statistics" element={<Statistics />} />
            <Route path="/trip-statistics" element={<TripStatisticsPage />} />
            <Route path="/trip-logs" element={<TripLogsPage />} />
            <Route path="/users" element={<Users />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </main>
      </div>
    </AuthContext.Provider>
  )
}

export default App
